import { listSessions, getSession } from '../recording/storage.js';

export interface LocalToolsOptions {
  storageDir: string;
  liveUrl: string;
}

export interface ToolDefinition {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

export const LOCAL_TOOL_DEFINITIONS: ToolDefinition[] = [
  {
    name: 'browserforge_list_sessions',
    description:
      'List recent browser sessions recorded by BrowserForge. ' +
      'Returns id, date, duration, action count, and first URL for each session.',
    inputSchema: {
      type: 'object',
      properties: {
        limit: {
          type: 'number',
          description: 'Maximum number of sessions to return (default: all)',
        },
        query: {
          type: 'string',
          description: 'Filter sessions by URL or session ID substring',
        },
      },
      required: [],
    },
  },
  {
    name: 'browserforge_get_session',
    description:
      'Get the full manifest for a specific BrowserForge session by ID, ' +
      'including every recorded action with tool name, arguments, and page URL.',
    inputSchema: {
      type: 'object',
      properties: {
        id: {
          type: 'string',
          description: 'Session ID to retrieve',
        },
      },
      required: ['id'],
    },
  },
  {
    name: 'browserforge_export_session',
    description:
      'Get the download URL for a BrowserForge session as a self-contained ZIP file. ' +
      'The ZIP contains session.json, all frame PNGs, and a README. ' +
      'Share the URL with users so they can download the session.',
    inputSchema: {
      type: 'object',
      properties: {
        id: {
          type: 'string',
          description: 'Session ID to export',
        },
      },
      required: ['id'],
    },
  },
  {
    name: 'browserforge_live_url',
    description:
      'Get the URL of the BrowserForge live viewer. ' +
      'Share this with users so they can watch the agent browse in real-time.',
    inputSchema: {
      type: 'object',
      properties: {},
      required: [],
    },
  },
];

const LOCAL_TOOL_NAMES = new Set(LOCAL_TOOL_DEFINITIONS.map((t) => t.name));

export class LocalToolsHandler {
  constructor(private readonly options: LocalToolsOptions) {}

  isLocal(toolName: string): boolean {
    return LOCAL_TOOL_NAMES.has(toolName);
  }

  handle(tool: string, args: Record<string, unknown>): Record<string, unknown> {
    switch (tool) {
      case 'browserforge_list_sessions': {
        const limit = typeof args['limit'] === 'number' ? args['limit'] : undefined;
        const query = typeof args['query'] === 'string' ? args['query'] : undefined;
        const { sessions } = listSessions(this.options.storageDir, { limit, query });
        const result = sessions.map((s) => ({
          id: s.id,
          date: s.startedAt,
          duration: s.duration,
          actionCount: s.actionCount,
          url: s.firstUrl,
        }));
        return { content: [{ type: 'text', text: JSON.stringify(result) }] };
      }

      case 'browserforge_get_session': {
        const id = args['id'];
        if (typeof id !== 'string' || !id) {
          return {
            isError: true,
            content: [{ type: 'text', text: 'Missing required parameter: id' }],
          };
        }
        const manifest = getSession(this.options.storageDir, id);
        if (!manifest) {
          return {
            isError: true,
            content: [{ type: 'text', text: `Session not found: ${id}` }],
          };
        }
        return { content: [{ type: 'text', text: JSON.stringify(manifest) }] };
      }

      case 'browserforge_export_session': {
        const id = args['id'];
        if (typeof id !== 'string' || !id) {
          return {
            isError: true,
            content: [{ type: 'text', text: 'Missing required parameter: id' }],
          };
        }
        const manifest = getSession(this.options.storageDir, id);
        if (!manifest) {
          return {
            isError: true,
            content: [{ type: 'text', text: `Session not found: ${id}` }],
          };
        }
        return {
          content: [{
            type: 'text',
            text: JSON.stringify({
              url: `/api/sessions/${id}/export`,
              message: `Download URL for session ${id}`,
            }),
          }],
        };
      }

      case 'browserforge_live_url': {
        return { content: [{ type: 'text', text: this.options.liveUrl }] };
      }

      default:
        return {
          isError: true,
          content: [{ type: 'text', text: `Unknown local tool: ${tool}` }],
        };
    }
  }
}
